import DataRow, { DataRowItem } from '@/libs/antd/components/DataRow';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { formatCurrency } from '@/libs/antd/formater';
import {
	getApprovalHistory,
	getProspectInformation,
} from '@/libs/fleet-api/services/prospect';
import {
	getVisitReportOfficePdfDetail,
	getVisitReportSitePdfDetail,
} from '@/libs/fleet-api/services/visit-reports';
import { FilePdfOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Col, Divider, Row, Table } from 'antd';
import dayjs from 'dayjs';
import { FC, Fragment, useState } from 'react';
import { useModals } from './ModalProvider';

export type ProspectDetailModalState = null | {
	prospectId: string;
};

export type ProspectDetailModalProps = {
	modal: ProspectDetailModalState;
	onClose: () => void;
};

const ProspectDetailModal: FC<ProspectDetailModalProps> = ({
	modal,
	onClose,
}) => {
	const { openLKPDFModal } = useModals();
	const { data, isLoading } = useQuery({
		queryKey: ['getProspectInformation', modal?.prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getProspectInformation(id ?? '');
		},
		enabled: !!modal?.prospectId,
	});

	const { data: approvalData, isLoading: approvalLoading } = useQuery({
		queryKey: ['getApprovalHistory', modal?.prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getApprovalHistory(id ?? '');
		},
		enabled: !!modal?.prospectId,
	});

	// get visit-report-detail data
	const {
		isError: visitReportOfficePdfError,
		isLoading: visitReportOfficePdfLoading,
	} = useQuery({
		queryKey: ['getVisitReportOfficePdfDetail', modal?.prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getVisitReportOfficePdfDetail(id ?? '');
		},
		enabled: !!modal?.prospectId,
	});

	// get visit-report-site-detail data
	const {
		isError: visitReportSitePdfError,
		isLoading: visitReportSitePdfLoading,
	} = useQuery({
		queryKey: ['getVisitReportSitePdfDetail', modal?.prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getVisitReportSitePdfDetail(id ?? '');
		},
		enabled: !!modal?.prospectId,
	});

	const approvalHistories = approvalData?.data || [];

	const getReferrerUnitName = (id: number) => {
		switch (id) {
			case 1:
				return 'Nama Dealer/Supplier';

			case 2:
				return 'BMRI Branch/Area';
			case 3:
				return 'Commercial Banking';
			case 4:
				return 'Department';
			case 5:
				return '-';
			default:
				return '';
		}
	};

	const {
		prospectPlafonDetails,
		prospectPlafons,
		prospectPlafonInformation: prospectInformation,
	} = data?.data || {};

	// const handleDownloadPDF = (type: string) => {
	// 	const input = document.getElementById(`${type}-pdf-content`);
	// 	// Specify the id of the element you want to convert to PDF
	// 	html2canvas(input!).then((canvas) => {
	// 		const imgData = canvas.toDataURL('image/png');
	// 		const pdf = new jsPDF();
	// 		const a4Width = 210; // A4 width in mm
	// 		const a4Height = (canvas.height * a4Width) / canvas.width; // Maintain aspect ratio
	// 		pdf.addImage(imgData, 'PNG', 0, 0, a4Width, a4Height); // Specify width and height
	// 		pdf.save(`lk-${type}.pdf`);
	// 		// Specify the name of the downloaded PDF file
	// 	});
	// };

	const items: DataRowItem[] = [
		[
			['Nama Customer', prospectInformation?.customerName],
			['ProspectID', prospectInformation?.prospectId],
		],
		[
			['Kelas Industri', prospectInformation?.industrySector.industryClass],
			['Sumber Order', prospectInformation?.orderSource],
		],
		[
			[
				'Portofolio Guideline',
				prospectInformation?.industrySector.portofolioGuideline,
			],
			[
				'SLIK Result',
				prospectInformation?.slikResult
					? 'Kol ' + prospectInformation?.slikResult
					: '-',
			],
		],
		[
			['FRM Name', prospectInformation?.frm?.value ?? '-'],
			['CA Name', prospectInformation?.creditAnalyst?.value ?? '-'],
		],
	];

	if (!visitReportOfficePdfError && !visitReportOfficePdfLoading) {
		items.push([
			[
				'Download LK Office',
				<a
					href="#"
					target="_blank"
					onClick={(e) => {
						e.preventDefault();
						openLKPDFModal({
							type: 'lk-office',
							prospectId: modal!.prospectId!,
						});
					}}
				>
					<FilePdfOutlined style={{ fontSize: 16, marginRight: 4 }} />
					Download PDF
				</a>,
			],
			['', ''],
		]);
	}

	if (!visitReportSitePdfError && !visitReportSitePdfLoading) {
		items[items.length - 1][1] = [
			'Download LK Site',
			<a
				href="#"
				target="_blank"
				onClick={(e) => {
					e.preventDefault();
					openLKPDFModal({
						type: 'lk-site',
						prospectId: modal!.prospectId!,
					});
				}}
			>
				<FilePdfOutlined style={{ fontSize: 16, marginRight: 4 }} />
				Download PDF
			</a>,
		];
	}

	items.push([
		['Pending User', prospectInformation?.pendingUser ?? '-'],
		['', ''],
	]);

	return (
		<FleetModal
			open={Boolean(modal)}
			width={940}
			cancelText="BACK"
			buttonPosition="end"
			closable={false}
			okButtonProps={{
				hidden: true,
			}}
			onCancel={() => onClose?.()}
		>
			<FleetTitle>View Detail Prospect</FleetTitle>
			<DataRow
				// loading={isLoading}
				items={items}
				style={{
					marginBottom: 20,
				}}
			/>
			<Divider />

			<FleetTitle>Informasi Limit Pembiayaan Existing</FleetTitle>
			<Table
				scroll={{ x: 'max-content' }}
				pagination={false}
				loading={isLoading}
				bordered
				dataSource={
					prospectPlafons?.existingFinancingLimitInformation.map(
						(record, key) => {
							return {
								...record,
								key,
							};
						},
					) ?? []
				}
				columns={[
					{
						title: 'No',
						render: (_, record) => record.no,
					},
					{
						title: 'No. MKL',
						render: (_, record) => record.napNo,
					},
					{
						title: 'Limit Pembiayaan Existing',
						render: (_, record) =>
							formatCurrency(record.existingFinancingLimit),
					},
					{
						title: 'Jenis Limit Pembiayaan',
						render: (_, record) => record.financingLimitType,
					},
					{
						title: 'Tipe Limit',
						render: (_, record) => record.financingLimitCharacteristic,
					},
					{
						title: 'Expired Date',
						render: (_, record) =>
							dayjs(record.expiredDate).format('DD/MM/YYYY'),
					},
					{
						title: 'NTF',
						render: (_, record) => formatCurrency(record.ntf) || 0,
					},
					{
						title: 'OS Principal',
						render: (_, record) => formatCurrency(record.outstandingPrincipal),
					},
					{
						title: 'Sisa Limit',
						render: (_, record) =>
							formatCurrency(record.outstandingFinancingLimit),
					},
					{
						title: 'Status',
						render: (_, record) => record.status,
					},
					// {
					// 	title: 'Select',
					// 	width: 1,
					// 	align: 'center',
					// 	render: () => (
					// 		<a style={{ display: 'block' }}>
					// 			<ArrowRightOutlined />
					// 		</a>
					// 	),
					// },
				]}
			/>

			<Divider />
			<FleetTitle>Informasi Limit Pengajuan Pembiayaan</FleetTitle>
			<Table
				scroll={{ x: 'max-content' }}
				bordered
				loading={isLoading}
				pagination={false}
				dataSource={
					prospectPlafons?.financingApplicationLimitInformation.map(
						(record, key) => {
							return {
								...record,
								key,
							};
						},
					) ?? []
				}
				columns={[
					{
						title: 'No',
						align: 'center',
						render: (_, record) => record.no,
					},
					{
						title: 'Limit Pembiayaan Existing',
						render: (_, record) =>
							formatCurrency(record.existingFinancingLimit),
					},
					{
						title: 'Jenis Limit Pembiayaan',
						render: (_, record) => record.financingLimitType,
					},
					{
						title: 'OS Principal',
						render: (_, record) => formatCurrency(record.outstandingPrincipal),
					},
					{
						title: 'Tujuan Pembiayaan',
						render: (_, record) => record.financingPurpose,
					},
					{
						title: 'Pengajuan Limit',
						render: (_, record) => formatCurrency(record.limitApplication),
					},
					{
						title: 'Total Pengajuan Plafon',
						render: (_, record) => formatCurrency(record.totalLimitApplication),
					},
					{
						title: 'Segmen Market',
						render: (_, record) => record.segmentMarket,
					},
				]}
			/>

			<Divider />

			{prospectPlafonDetails && prospectPlafonDetails.length > 0 && (
				<Fragment>
					{prospectPlafonDetails.map((detail, index) => (
						<Fragment key={detail.prospectPlafonId}>
							<FleetTitle>{`Detail Limit Pengajuan Pembiayaan Plafond ${detail.plafonType?.value ?? '-'}`}</FleetTitle>
							<DataRow
								loading={isLoading}
								items={[
									[
										['Segmen Market', detail.segmentMarket?.value ?? '-'],
										['Sumber Order', detail.orderSource?.value ?? '-'],
									],
									[
										[
											getReferrerUnitName(detail.orderSource?.id),
											<div>
												{detail.referrer?.unitName ?? '-'}
												{detail.referrer?.unitGroup ? (
													<div style={{ fontSize: 14, color: '#bababa' }}>
														{detail.referrer?.unitGroup}
													</div>
												) : null}
												{detail.referrer?.unitBuc ? (
													<div style={{ fontSize: 14, color: '#bababa' }}>
														{detail.referrer?.unitBuc}
													</div>
												) : null}
											</div>,
										],
										['Nama Pemberi Refferal', detail.referrer?.name ?? '-'],
									],
									[
										[
											'Jabatan Pemberi Refferal',
											detail.referrer.position?.value ?? '-',
										],
										['Nomor Telepon', detail.referrer?.phoneNumber ?? '-'],
									],
									[
										[
											'Pengajuan Limit',
											formatCurrency(detail.limitApplication) ?? '-',
										],
										['Jenis Plafon', detail.plafonType?.value ?? '-'],
									],
									[['Tujuan Pembiayaan', detail.orderType?.value ?? '-']],
								]}
								style={{
									marginBottom: 20,
								}}
							/>
							<Row>
								<Col
									span={5}
									style={{ fontWeight: 600, fontSize: 14 }}
								>
									Jenis Pembiayaan
								</Col>
								<Col
									span={19}
									style={{ marginLeft: -8 }}
								>
									{detail.financingTypes
										.map((type) => type.parent.value)
										.join(', ') || '-'}
								</Col>
							</Row>
							<Divider />
							<Row style={{ marginTop: 8 }}>
								<Col
									span={5}
									style={{ fontWeight: 600, fontSize: 14 }}
								>
									Jenis Fasilitas
								</Col>
								<Col
									span={19}
									style={{ marginLeft: -8 }}
								>
									{detail.financingTypes
										.flatMap((type) => type.items ?? [])
										.map((item) => item.value)
										.join(', ') || '-'}
								</Col>
							</Row>
							<Divider />
							<Row>
								<Col
									span={5}
									style={{ fontWeight: 600, fontSize: 14 }}
								>
									Object Pembiayaan
								</Col>
								<Col
									span={19}
									style={{ marginLeft: -8 }}
								>
									{detail.financingObjects
										?.map((i, j) => (j === 0 ? i.value : ', ' + i.value))
										.join('') ?? '-'}
								</Col>
							</Row>
							{index < prospectPlafonDetails.length - 1 && <Divider />}
						</Fragment>
					))}
				</Fragment>
			)}
			{approvalHistories.length > 0 && (
				<Fragment>
					<Divider />
					<FleetTitle>Approval History</FleetTitle>
					<Table
						dataSource={
							approvalHistories?.map((record, key) => {
								return { ...record, key };
							}) ?? []
						}
						columns={[
							{
								title: 'No',
								width: 1,
								align: 'center',
								dataIndex: 'no',
							},
							{
								title: 'Name',
								dataIndex: 'approvalBy',
							},
							{
								title: 'Date',
								dataIndex: 'approvalDate',
							},
							{
								title: 'Process',
								dataIndex: 'approvalProcess',
							},
							{
								title: 'Status',
								align: 'center',
								render: (_, record) => {
									return (
										<span
											className={`review-status ${record.approvalStatus?.toLowerCase()}`}
										>
											{record.approvalStatus}
										</span>
									);
								},
							},
							{
								title: 'Approval Notes',
								render: (_, record) => {
									if (record.rejectCategory) {
										return (
											<span>
												{record.approvalNote}
												<br />
												{record.rejectCategory?.value}
											</span>
										);
									}
									return <span>{record.approvalNote}</span>;
								},
							},
						]}
						bordered
						pagination={false}
						loading={approvalLoading}
						style={{ marginBottom: 20 }}
					/>
				</Fragment>
			)}
		</FleetModal>
	);
};

export default ProspectDetailModal;

export const useProspectDetailModal = () => {
	return useState<ProspectDetailModalState>(null);
};
